import { z } from 'zod';
import { DefaultSubtypeValues, SubtypeProperties } from '../../../../../../../react-commons/hooks/useSubtypeFormNames';
import { assertUnreachable } from '../../../../../../../react-commons/utils/utilFunctions';
import { getMutationSchema } from '../../../../../ui/util/getMutationSchema';
import { ResourceType } from '../../../../enumerations/ResourceType';
import { VCycleElementType, VCycleElementTypeSchema } from '../../../../enumerations/VCycleElementType';
import { useIndirectlyVersionedResourceAdapter } from '../../../../resource-base/adapters/useIndirectlyVersionedResourceAdapter';
import { useMultiAndSingleResourceMultiParentedResourceAdapterActions } from '../../../../resource-base/adapters/useMultiAndSingleResourceMultiParentedResourceAdapterActions';
import {
    createIndirectlyVersionedMultiParentedArtifactResourceSchema,
    defaultIndirectlyVersionedArtifactResource,
} from '../../../../resource-base/artifactResourceTypes';
import { IdSchema } from '../../../../resource-base/baseSchemas';
import { copyOverExistingProperties } from '../../../utilFunctions';

export enum VCycleElementPropertyValidation {
    TYPE = 'TYPE',
    PROCESS_AREA = 'PROCESS_AREA',
    REPRESENTATION = 'REPRESENTATION',
}

export enum VCycleElementParentRelation {
    V_CYCLE_REPRESENTATION_V_CYCLE_ELEMENT_CONTAINER = 'V_CYCLE_REPRESENTATION_V_CYCLE_ELEMENT_CONTAINER',
    PROCESS_AREA_V_CYCLE_ELEMENT_CONTAINER = 'PROCESS_AREA_V_CYCLE_ELEMENT_CONTAINER',
}

const VCycleElementParentRelationSchema = z.enum(VCycleElementParentRelation);

const VCycleElementResourceBaseSchema = createIndirectlyVersionedMultiParentedArtifactResourceSchema(
    VCycleElementParentRelationSchema
).extend({
    type: VCycleElementTypeSchema,
    resourceType: z.literal(ResourceType.V_CYCLE_ELEMENT).default(ResourceType.V_CYCLE_ELEMENT),
});

const ProcessAreaVCycleElementResourceSchema = VCycleElementResourceBaseSchema.extend({
    type: z.literal(VCycleElementType.PROCESS_AREA),
    processArea: IdSchema,
});

const VCycleRepresentationVCycleElementResourceSchema = VCycleElementResourceBaseSchema.extend({
    type: z.literal(VCycleElementType.REPRESENTATION),
    representation: IdSchema,
});

export const VCycleElementResourceSchema = z.union([
    ProcessAreaVCycleElementResourceSchema,
    VCycleRepresentationVCycleElementResourceSchema,
]);

export const VCycleElementMutationResourceSchema = z.union([
    getMutationSchema(ProcessAreaVCycleElementResourceSchema),
    getMutationSchema(VCycleRepresentationVCycleElementResourceSchema),
]);

type ProcessAreaVCycleElementResource = z.infer<typeof ProcessAreaVCycleElementResourceSchema>;
type VCycleRepresentationVCycleElementResource = z.infer<typeof VCycleRepresentationVCycleElementResourceSchema>;
export type VCycleElementResource = z.infer<typeof VCycleElementResourceSchema>;

export const vcycleElementDefaultSubtypeValues: DefaultSubtypeValues<VCycleElementResource> = {
    processArea: '',
    representation: '',
};

export const vcycleElementSubTypeProperties: SubtypeProperties<VCycleElementResource, 'type'> = {
    [VCycleElementType.PROCESS_AREA]: {
        processArea: 'processArea',
    },
    [VCycleElementType.REPRESENTATION]: {
        representation: 'representation',
    },
};

export function getDefaultVCycleElementResource(
    type: VCycleElementType.PROCESS_AREA,
    previousResource?: VCycleElementResource
): ProcessAreaVCycleElementResource;

export function getDefaultVCycleElementResource(
    type: VCycleElementType.REPRESENTATION,
    previousResource?: VCycleElementResource
): VCycleRepresentationVCycleElementResource;

export function getDefaultVCycleElementResource(
    type: VCycleElementType,
    previousResource?: VCycleElementResource
): VCycleElementResource;

export function getDefaultVCycleElementResource(
    type: VCycleElementType,
    previousResource?: VCycleElementResource
): VCycleElementResource {
    const baseResource: z.infer<typeof VCycleElementResourceBaseSchema> = {
        ...defaultIndirectlyVersionedArtifactResource,
        resourceType: ResourceType.V_CYCLE_ELEMENT,
        parentRelation: VCycleElementParentRelation.PROCESS_AREA_V_CYCLE_ELEMENT_CONTAINER,
        type,
    };

    switch (type) {
        case VCycleElementType.PROCESS_AREA:
            const valueBase = {
                ...baseResource,
                parentRelation: VCycleElementParentRelation.PROCESS_AREA_V_CYCLE_ELEMENT_CONTAINER,
                processArea: '',
            };
            return {
                ...copyOverExistingProperties(valueBase, previousResource),
                type,
            };
        case VCycleElementType.REPRESENTATION:
            const structBase = {
                ...baseResource,
                parentRelation: VCycleElementParentRelation.V_CYCLE_REPRESENTATION_V_CYCLE_ELEMENT_CONTAINER,
                representation: '',
            };
            return {
                ...copyOverExistingProperties(structBase, previousResource),
                type,
            };
        default:
            return assertUnreachable(type);
    }
}

export const defaultVCycleElementResource: VCycleElementResource = getDefaultVCycleElementResource(
    VCycleElementType.PROCESS_AREA
);

export function useVCycleElementActions() {
    const actions = {
        ...useMultiAndSingleResourceMultiParentedResourceAdapterActions(
            'project/v-cycle-element',
            ResourceType.V_CYCLE_ELEMENT,
            VCycleElementResourceSchema
        ),
        ...useIndirectlyVersionedResourceAdapter(
            'project/v-cycle-element',
            ResourceType.V_CYCLE_ELEMENT,
            VCycleElementResourceSchema
        ),
    };

    return {
        ...actions,
    };
}
